import { useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * Default overlay region: below a 4rem top bar and to the right of a 16rem sidebar (md+).
 * Aligns with `h-topnav` / `w-sidebar` in app shells. Uses literal lengths so consumers
 * do not need matching `spacing` keys in Tailwind.
 *
 * Pass `backdropClassName` to extend the region (e.g. Storybook). Avoid `!inset-0` in the core shell if you
 * want the dim/blur overlay to stay off the top bar and sidebar (default region already excludes them).
 */
const MODAL_LAYOUT_DEFAULT_REGION_CLASSNAME =
  'left-0 right-0 bottom-0 top-[var(--modal-topbar-height,4rem)] md:left-[var(--modal-sidebar-offset,16rem)] transition-[left] duration-200 ease-in-out';

/**
 * Modal shell: dimmed backdrop and a panel centered in the same region.
 * Compose toolbar, scroll areas, footers, etc. as children. Matches app patterns
 * (border-edge, surface tokens).
 *
 * By default renders in place. Set **`useBodyPortal`** to mount with `createPortal(..., document.body)`
 * so `position: fixed` is not clipped by ancestor `overflow` / `transform` (e.g. Storybook canvas).
 *
 * Header slots:
 *   - `breadcrumb`   – small muted text rendered above the title (e.g. "RC-001 · Edit")
 *   - `titlePrefix`  – ReactNode rendered to the left of the title block (e.g. a colored icon)
 *   - `headerRight`  – ReactNode rendered between the title area and the close button
 *                      (e.g. a context pill or a badge like "RC-0003")
 */
const ModalLayout = ({
  isOpen,
  onClose,
  title,
  subtitle,
  breadcrumb,
  titlePrefix,
  headerRight,
  titleId = 'modal-layout-title',
  closeAriaLabel = 'Close',
  closeDisabled = false,
  children,
  backdropClassName = '',
  panelClassName = '',
  maxWidthClassName = 'max-w-2xl',
  heightClassName = '',
  fillHeight = false,
  useBodyPortal = false,
}) => {
  const resolvedHeightClassName = heightClassName || (fillHeight ? 'h-[min(90vh,48rem)]' : '');
  const handleBackdropClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget && !closeDisabled) onClose?.();
    },
    [onClose, closeDisabled],
  );

  const regionClassName = [
    MODAL_LAYOUT_DEFAULT_REGION_CLASSNAME,
    backdropClassName,
  ]
    .filter(Boolean)
    .join(' ');

  if (!isOpen) return null;

  const modalTree = (
    <>
      <div
        className={[
          'fixed z-50 bg-black/60 backdrop-blur-sm',
          regionClassName,
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={handleBackdropClick}
        role="presentation"
      />

      <div
        className={[
          'fixed z-[51] flex items-center justify-center pointer-events-none p-4',
          regionClassName,
        ]
          .filter(Boolean)
          .join(' ')}
        role="presentation"
      >
        <div
          className={[
            'pointer-events-auto w-full flex flex-col bg-surface rounded-xl border border-edge shadow-2xl overflow-hidden',
            maxWidthClassName,
            resolvedHeightClassName,
            panelClassName,
          ]
            .filter(Boolean)
            .join(' ')}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
        >
          <div className="flex shrink-0 items-center justify-between border-b border-edge px-5 py-4">
            <div className="flex items-center gap-3">
              {titlePrefix}
              <div>
                {breadcrumb ? (
                  <p className="mb-0.5 text-[11px] text-content-muted">{breadcrumb}</p>
                ) : null}
                <h2 id={titleId} className="text-base font-semibold text-content">
                  {title}
                </h2>
                {subtitle ? (
                  <p className="mt-0.5 text-xs text-content-muted">{subtitle}</p>
                ) : null}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {headerRight}
              {/* `closeDisabled` covers the X too, not only the backdrop: while
                  the dialog is busy (saving, deleting) every way out is shut,
                  and the X has to look it. */}
              <button
                type="button"
                aria-label={closeAriaLabel}
                disabled={closeDisabled}
                onClick={() => {
                  if (!closeDisabled) onClose?.();
                }}
                className="text-content-muted hover:text-content transition-colors p-1 -mr-1 disabled:pointer-events-none disabled:opacity-40"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col">{children}</div>
        </div>
      </div>
    </>
  );

  if (useBodyPortal && typeof document !== 'undefined') {
    return createPortal(modalTree, document.body);
  }

  return modalTree;
};

export default ModalLayout;
